import { useState } from 'react';
import styled from 'styled-components';
import { useAssembleGeoDesignMutation, useLazyGetGeoCompositeUnderlayQuery } from '@shared/api';
import {
  ASSEMBLY_LEVELS,
  centerByLabel,
  fmtCoord,
  fmtNumber,
  sheetRoleLabel,
  thetaByLabel,
  type GeoAssembly,
} from '@entities/geodesy';
import { Button } from '@shared/ui/Button';
import { Input } from '@shared/ui/Input';
import {
  ErrorText,
  FieldGrid,
  FieldLabel,
  Hint,
  LinkButton,
  Muted,
  Notice,
  NumInput,
  Stack,
  useAction,
} from '@widgets/GeoSchemes';
import { useWizard } from './wizard';

const Sub = styled.h4`
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1.5)};
  padding: ${({ theme }) => theme.spacing(3)};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surface};
`;

const Mark = styled.strong`
  font-size: 13.5px;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Small = styled.ul`
  margin: 0;
  padding-left: ${({ theme }) => theme.spacing(5)};
  font-size: 12.5px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

/** «−2,550», «99,950» — отметка в метрах, три знака, как на чертежах. */
function level(v: number): string {
  return fmtCoord(v, 3);
}

/**
 * «Собрать из листов» (шаг 6, ТЗ AI-GEO-2026 v2.1, §3): основной лист — схема
 * расположения — и листы комплекта уходят в сборку: марка с чертежа садится на
 * своё место схемы с поворотом, ярусы получают отметки с разреза (низ — со
 * схемы), анкерные болты — свои отметки и допуск. Числа считает программа.
 */
export function AssemblePanel({ confirmDiscard }: { confirmDiscard: () => boolean }) {
  const { scheme, readOnly, goTo } = useWizard();
  const [assemble] = useAssembleGeoDesignMutation();
  const [loadComposite] = useLazyGetGeoCompositeUnderlayQuery();
  const running = useAction();
  const opening = useAction();
  const [mark, setMark] = useState('');
  const [baseLevel, setBaseLevel] = useState<number | null>(null);
  const [zeroBs, setZeroBs] = useState<number | null>(null);
  const extra = scheme.sheets ?? [];
  const assembly = scheme.design.assembly ?? null;
  if (!scheme.multi_sheet || !scheme.sheet || (extra.length === 0 && !assembly)) return null;
  const bound =
    scheme.settings.binding.mode === 'sheet' && scheme.settings.binding.control_points.length >= 2;

  function run() {
    if (scheme.design.elements.length > 0 && scheme.design.source !== 'assembly') {
      if (
        !window.confirm(
          'Проектные элементы заменятся конструкцией, собранной из листов. Продолжить?',
        )
      )
        return;
    }
    if (!confirmDiscard()) return;
    void running.run(
      () =>
        assemble({
          id: scheme.id,
          mark: mark.trim() || null,
          base_level: baseLevel,
          zero_bs: zeroBs,
        }).unwrap(),
      'Конструкция из листов не собралась.',
    );
  }

  function openComposite() {
    void opening.run(async () => {
      const blob = await loadComposite(scheme.id, false).unwrap();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    }, 'Подложка сборки не открылась.');
  }

  return (
    <Stack $gap={3}>
      <Sub>Собрать из листов комплекта</Sub>
      {!readOnly && (
        <>
          <Hint>
            Схема расположения даёт место марки и отметку низа, чертёж марки — подошву, подколонник,
            ниши и болты, разрез — высоты ярусов. Марка садится на схему с поворотом, отметки ярусов
            считаются от низа. Пустые поля — программа найдёт на листах сама.
          </Hint>
          {!bound && (
            <Notice $tone="warning" role="status">
              Сначала привяжите основной лист к координатам площадки.{' '}
              <LinkButton type="button" onClick={() => goTo(2)}>
                Привязка листа — шаг 2 →
              </LinkButton>
            </Notice>
          )}
          {extra.length === 0 && (
            <Notice $tone="info" role="status">
              Добавьте чертёж марки в «Листы комплекта».{' '}
              <LinkButton type="button" onClick={() => goTo(1)}>
                Шаг 1 →
              </LinkButton>
            </Notice>
          )}
          <FieldGrid $min={180}>
            <FieldLabel>
              Марка
              <Input
                value={mark}
                placeholder="все со съёмкой"
                onChange={(e) => setMark(e.target.value)}
              />
            </FieldLabel>
            <FieldLabel>
              Отметка низа от 0,000, м
              <NumInput value={baseLevel} onValue={setBaseLevel} placeholder="например, −2,550" />
            </FieldLabel>
            <FieldLabel>
              0,000 = … БС, м
              <NumInput value={zeroBs} onValue={setZeroBs} placeholder="например, 100,60" />
            </FieldLabel>
          </FieldGrid>
          <div>
            <Button
              type="button"
              $variant="secondary"
              $size="sm"
              onClick={run}
              disabled={running.busy || !bound || extra.length === 0}
            >
              {running.busy ? 'Собираем — до двух минут…' : 'Собрать из листов'}
            </Button>
          </div>
          {running.error && <ErrorText role="alert">{running.error}</ErrorText>}
        </>
      )}
      {assembly && <AssemblySummary assembly={assembly} onOpen={openComposite} />}
      {opening.error && <ErrorText role="alert">{opening.error}</ErrorText>}
    </Stack>
  );
}

function AssemblySummary({ assembly, onOpen }: { assembly: GeoAssembly; onOpen: () => void }) {
  const total = assembly.timings['всего'];
  return (
    <Stack $gap={2}>
      <Muted>
        {assembly.zero_bs != null
          ? `0,000 = ${level(assembly.zero_bs)} БС${assembly.zero_text ? ` (${assembly.zero_text})` : ''}`
          : 'абсолютная отметка 0,000 не найдена — впишите её и соберите заново'}
        {total != null && ` · собрано за ${fmtNumber(total, 1)} с`}
      </Muted>
      {assembly.structures.map((s) => (
        <Card key={`${s.mark}:${s.instance}`}>
          <Mark>
            {s.mark}
            {s.instance > 1 ? ` (${s.instance}-я на схеме)` : ''}
            {s.detail_designation ? ` · ${s.detail_designation}` : ''}
          </Mark>
          <Muted>
            поворот {fmtNumber(s.theta_deg, 0)}° — {thetaByLabel(s.theta_by)}
            {centerByLabel(s.center_by) ? `; центр — ${centerByLabel(s.center_by)}` : ''}
            {s.node_label
              ? `; узел изометрии «${s.node_label}»${
                  s.node_delta_m != null ? ` в ${fmtNumber(s.node_delta_m * 1000, 0)} мм` : ''
                }`
              : ''}
            ; ярусов {s.tiers}, болтов {s.bolts}
            {s.section ? ` · высоты по «${s.section}»` : ''}
          </Muted>
          {s.levels.length > 0 && (
            <Small>
              {s.levels.map((lv) => (
                <li key={lv.name}>
                  {ASSEMBLY_LEVELS[lv.name] ?? lv.name}: {level(lv.rel)}
                  {lv.abs != null ? ` (${level(lv.abs)} БС)` : ''}
                </li>
              ))}
            </Small>
          )}
          {[...s.checks, ...s.warnings].length > 0 && (
            <Small>
              {s.checks.map((c) => (
                <li key={c}>{c}</li>
              ))}
              {s.warnings.map((w) => (
                <li key={w}>⚠ {w}</li>
              ))}
            </Small>
          )}
        </Card>
      ))}
      <Muted>
        Листы:{' '}
        {assembly.sheets
          .map((sh) => `${sh.designation || sh.file_name} — ${sheetRoleLabel(sh.role)}`)
          .join('; ')}
      </Muted>
      {assembly.composite_file_id && (
        <div>
          <LinkButton type="button" onClick={onOpen}>
            Открыть подложку сборки (PDF)
          </LinkButton>
        </div>
      )}
    </Stack>
  );
}
